/**
 * Country Status Pill
 *
 * Displays the current delivery country (auto-detected or user-selected)
 * and provides a quick entry point to change it.
 */
import type React from 'react';
import { AlertCircle, MapPin } from 'lucide-react';
import { PillButton, Tooltip, TooltipContent, TooltipTrigger } from '@/ui';
import {
    useCountryCode,
    useCountryDetectionError,
    useCountryDetectionStatus,
    useCountryModalActions,
    useCountryName,
    useCountrySource,
    useCurrency,
} from '../hooks';

interface CountryStatusProps {
    className?: string;
}

export function CountryStatus({ className }: CountryStatusProps): React.ReactElement {
    const countryCode = useCountryCode();
    const countryName = useCountryName();
    const currency = useCurrency();
    const countrySource = useCountrySource();
    const detectionStatus = useCountryDetectionStatus();
    const detectionError = useCountryDetectionError();
    const { open } = useCountryModalActions();

    if (detectionStatus === 'pending' && !countryCode) {
        return (
            <PillButton
                disabled
                variant="loading"
                className={className}
                leftIcon={<MapPin />}
            >
                Detecting location…
            </PillButton>
        );
    }

    if (detectionStatus === 'error' && !countryCode) {
        return (
            <PillButton
                onClick={open}
                variant="destructive"
                className={className}
                leftIcon={<AlertCircle />}
            >
                {detectionError ?? 'Set delivery country'}
            </PillButton>
        );
    }

    const label = countryName ?? countryCode ?? 'Set delivery country';
    const sourceLabel =
        countrySource === 'checkout'
            ? 'Based on shipping address'
            : countrySource === 'manual'
                ? 'Selected'
                : 'Auto-detected';

    return (
        <Tooltip>
            <TooltipTrigger asChild>
                <PillButton
                    onClick={open}
                    variant="glass"
                    className={className}
                    aria-label="Change delivery country"
                    leftIcon={<MapPin />}
                    truncate
                >
                    {label}
                    {currency ? ` · ${currency}` : ''}
                </PillButton>
            </TooltipTrigger>
            <TooltipContent side="bottom" size="sm">
                {sourceLabel}
            </TooltipContent>
        </Tooltip>
    );
}
